import { Box, SxProps, Theme } from '@mui/material';
import { ReactNode } from 'react';
import { PanelFooter } from './PanelFooter';
import { PanelHeader } from './PanelHeader';

interface PanelProps {
  /**
   * Sentence-case panel title (e.g. "Transcripción").
   * Omit when the title lives outside the panel (e.g. a ContentCluster label).
   */
  title?: ReactNode;
  /** Right-aligned action icons (search, download, etc.). */
  actions?: ReactNode;
  children: ReactNode;
  /**
   * Optional sticky footer (typically action buttons). Renders below a scrollable
   * body so it stays pinned when the panel has a fixed height.
   */
  footer?: ReactNode;
  /** Styles merged into the outer shell (height, maxHeight, etc.). */
  sx?: SxProps<Theme>;
}

/**
 * Self-contained content pane: bordered shell (no elevation), optional header,
 * scrollable body, and optional sticky footer. Use for side panels and detail panes.
 * Pass no `title` when the section label is rendered outside (headerless body shell).
 */
export function Panel({ title, actions, children, footer, sx }: PanelProps) {
  const hasFooter = footer != null;
  const hasHeader = title != null || actions != null;

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        ...sx,
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          pt: 1.5,
          px: 1.5,
          // Footer owns bottom padding; without it, keep the same inset so body
          // content doesn't sit flush against the panel border.
          ...(!hasFooter ? { pb: 1.5 } : {}),
        }}
      >
        {hasHeader && <PanelHeader title={title} actions={actions} />}
        {hasFooter ? (
          <>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {children}
            </Box>
            <PanelFooter>{footer}</PanelFooter>
          </>
        ) : (
          children
        )}
      </Box>
    </Box>
  );
}
